import React, { useState, createContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Logger from '../utils/logger';
import useIsomorphicEffect from '../hooks/useIsomorphicEffect';
import { createEvent } from '../utils/events';
import { env } from '../utils/util';

export const YMapContext = createContext();

const initialState = {
  points: [],
  reactify: null,
  ymaps: null,
  packages: {},
  errors: [],
};

let loadPromise = null;
const YMapAPIKey = env('REACT_APP_MAP_API_KEY');

export const YMapPackage = {
  CartesianProjection: "@yandex/ymaps3-cartesian-projection@0.0.1",
  Clusterer: "@yandex/ymaps3-clusterer@0.0.1",
  Controls: "@yandex/ymaps3-controls@0.0.1",
  Hint: "@yandex/ymaps3-hint@0.0.1",
  Markers: "@yandex/ymaps3-markers@0.0.1",
  SphericalMercatorProjection: "@yandex/ymaps3-spherical-mercator-projection@0.0.1",
  Geoobjects: "@yandex/ymaps3-geoobjects@0.0.1",
  Geocoder: "@yandex/ymaps3-geocoder@0.0.1",
  UI: "@yandex/ymaps3-default-ui-theme@0.0.1",
};

const initPackages = Object.entries(YMapPackage).reduce((acc, [, value]) => {acc[value] = null; return acc;}, {});

export const YMapProvider = ({ apiKey = YMapAPIKey, lang, apiKeys, coordorder, mode, onLoad = () => {}, onError = () => {}, children, reuseLoadPromise = true }) => {
  const logger = new Logger({ name: 'YMapProvider', showDate: false, showFile: false });
  const {t} = useTranslation(['common']);

  const [ymapInfo, setYMapInfo] = useState(initialState);
  const [packages, setPackages] = useState(initPackages);
  const [loaded, setLoaded] = useState(false);

  const _setStore = (field, value) => {
    setYMapInfo((prevState) => ({ ...prevState, [field]: value }));
  };

  const _useStore = (...args) => {
    const [field] = args;

    useEffect(() => {
      if (args.length > 1) {
        _setStore(field, args[1]);
      } else {
        _setStore(field, ymapInfo[field]);
      }
    }, []);

    return [ymapInfo[field], (value) => _setStore(field, value)];
  };

  const _setPackage = (field, value) => {
    setPackages((prevState) => ({ ...prevState, [field]: value }));
  };

  const _usePackage = (...args) => {
    const [field] = args;

    useEffect(() => {
      if (args.length > 1) {
        _setPackage(field, args[1]);
      } else {
        _setPackage(field, packages[field]);
      }
    }, []);

    return [packages[field], (value) => _setPackage(field, value)];
  };

  const ymaps3 = (type) => {
    if (type) {
      return ymapInfo[type];
    } else {
      return { reactify: ymapInfo.reactify, ymaps: ymapInfo.ymaps };
    }
  };

  const initYmaps = async ({key, lang = "ru_RU", coordorder, mode, apiKeys, reuseLoadPromise}) => {
    loadPromise = (reuseLoadPromise && loadPromise) || new Promise(async (resolve, reject) => {
      try {
        if (window && window.ymaps3) {
          const ymaps = window.ymaps3;
          await ymaps.ready;
          const ymaps3Reactify = await ymaps.import("@yandex/ymaps3-reactify");
          const reactify = ymaps3Reactify.reactify.bindTo(React, ReactDOM);

          resolve({
            ymaps,
            reactify,
          });
        } else if (document && window) {
          const script = document.createElement("script");
          document.body.appendChild(script);
          script.type = "text/javascript";

          const query = new URLSearchParams({
            apikey: key,
            lang: lang,
          });

          if (coordorder) {
            query.set('coordorder', coordorder);
          }

          if (mode) {
            query.set('mode', mode);
          }

          script.src = `https://api-maps.yandex.ru/v3/?${query.toString()}`;
          script.onload = async () => {
            const ymaps = window.ymaps3;
            await ymaps.ready;

            if (apiKeys) {
              ymaps.getDefaultConfig().setApikeys(apiKeys);
            }

            const ymaps3Reactify = await ymaps.import("@yandex/ymaps3-reactify");
            const reactify = ymaps3Reactify.reactify.bindTo(React, ReactDOM);

            resolve({
              ymaps,
              reactify,
            });
          };

          script.onerror = reject;
        } else {
          reject();
        }
      } catch(e){
        reject(e);
      }
    });

    return loadPromise;
  };

  useIsomorphicEffect(() => {
    initYmaps({ key: apiKey, lang, coordorder, mode, apiKeys, reuseLoadPromise }).then((result) => {
      _setStore('ymaps', result.ymaps);
      _setStore('reactify', result.reactify);
      setLoaded(true);
      onLoad?.(result);
      createEvent('ymaps-ready');
    }).catch((e) => {
      logger.error(e);
      setLoaded(false);
      onError?.(e)
    });
  }, []);

  useEffect(() => {
  }, []);


  return (
    <>
      {loaded ? (
        <YMapContext.Provider
          value={{
            useState: _useStore,
            usePackage: _usePackage,
            ymaps3
          }}
        >
          {children}
        </YMapContext.Provider>
      ) : (<></>)}
    </>
  );
};

YMapProvider.propTypes = {
  apiKey: PropTypes.string,
  lang: PropTypes.string,
  apiKeys: PropTypes.any,
  coordorder: PropTypes.oneOf(['latlong', 'longlat']),
  mode: PropTypes.oneOf(['release', 'debug']),
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.element.isRequired,
  reuseLoadPromise: PropTypes.bool,
};
